import React, { useRef, useState } from "react";
import { Box, Card, Grid, LinearProgress, Typography } from "@mui/material";
import Highcharts from "highcharts";
import more from "highcharts/highcharts-more";
import HighchartsReact from "highcharts-react-official";
import { styled } from "@mui/system";
import TitleBar from "components/TitleBar";
import AssetError from "./AssetError";

if (typeof more === "function") {
    more(Highcharts);
}

const RoundedLinearProgress = styled(LinearProgress)(({ theme, barcolor }) => ({
    width: "100%",
    height: 16,
    borderTopRightRadius: "5px",
    borderBottomRightRadius: "5px",
    [`& .MuiLinearProgress-bar`]: {
        borderRadius: 0,
        backgroundColor: barcolor,
    },
}));

const Schedule = ({ scheduleKpi, isLoading, error, assetError, customer }) => {
    const chartRef = useRef(null);
    const [speed, setSpeed] = useState(1);
    let progress = 0;


    let apiData = {};

    if (customer?.title == 'Safexpress') {
        progress = 1;
        apiData = {
            ...scheduleKpi?.data?.[0],
            actualProgress: 0.7,
            planProgress: 1.5
        }
    } else {
        apiData = scheduleKpi?.data?.[0]
    }

    const actual = apiData?.actualProgress ?? 0;
    const plan = apiData?.planProgress ?? 1;

    const clampedValue = progress == 0 ? Math.min(speed, (actual / plan)) : Math.min(speed, 0.47);

    const totalMilestones = apiData?.totalMilestonesCount ?? 0;
    const completedlMilestones = apiData?.achievedMilestonesCount ?? 0;
    const missedMilestones = apiData?.missedMilestonesCount ?? 0;

    const chartOptions = {
        chart: {
            type: "gauge",
            plotBackgroundColor: null,
            plotBackgroundImage: null,
            plotBorderWidth: 0,
            plotShadow: false,
            height: "53%",
        },
        title: {
            text: "",
        },
        pane: {
            startAngle: -90,
            endAngle: 89.9,
            background: null,
            center: ["50%", "95%"],
            size: "180%",
        },
        yAxis: {
            min: 0,
            max: 2,
            tickPixelInterval: 72,
            tickPosition: "inside",
            tickColor: Highcharts.defaultOptions.chart.backgroundColor || "#FFFFFF",
            tickLength: 20,
            tickWidth: 0,
            minorTickInterval: null,
            labels: {
                enabled: false,
                // distance: 20,
                style: {
                    fontSize: "14px",
                },
            },
            lineWidth: 0,
            plotBands: [
                {
                    from: 0,
                    to: 0.5,
                    color: "#E65F2B",   // red
                    thickness: 40,
                    borderRadius: '2px'
                },
                {
                    from: 0.5,
                    to: 1,
                    color: "#E5AE21",   // light red
                    thickness: 40,
                    borderRadius: '2px'
                },
                {
                    from: 1,
                    to: 1.5,
                    color: "#45B056",   // yellow
                    thickness: 40,
                    borderRadius: '2px'
                },
                {
                    from: 1.5,
                    to: 2,
                    color: "#1A932E",   // green
                    thickness: 40,
                    borderRadius: '2px'
                }
            ]

        },
        credits: {
            enabled: false,
        },
        series: [
            {
                name: "Schedule Performance Index",
                data: [clampedValue],
                tooltip: {
                    valueSuffix: "",
                    pointFormat: '<span style="color:{point.color}">●</span> {series.name}: <b>{point.y:.2f}</b>'
                },
                dataLabels: {
                    enabled: true,
                    formatter: function () {
                        return `<div>${clampedValue.toFixed(2)}</div>`;
                    },
                    borderWidth: 0,
                    y: -60,
                    // x:20,
                    style: {
                        fontSize: "30px",
                        color:
                            (Highcharts.defaultOptions.title &&
                                Highcharts.defaultOptions.title.style &&
                                Highcharts.defaultOptions.title.style.color) ||
                            "#333333",
                    },
                },
                dial: {
                    radius: "80%",
                    backgroundColor: "gray",
                    baseWidth: 10,
                    baseLength: "0%",
                    rearLength: "0%",
                },
                pivot: {
                    backgroundColor: "gray",
                    radius: 6,
                },
            },
        ],
    };


    return (

        <Card
            elevation={0}
            sx={{
                borderRadius: 3,
                border: "1px solid #E4E4E4",
                width: '100%',
                pb: 2,
                height: "100%",
                boxSizing: "border-box"
            }}
        >
            <TitleBar title={"Schedule Overview"} description={"Track schedule efficiency in real time."} />
            {!isLoading && !error && apiData && <Grid container alignItems={"center"}>
                <Grid size={{ xs: 12, md: 5 }}>
                    {/* <HighchartsReact
                        highcharts={Highcharts}
                        options={options}
                    /> */}

                    <Box sx={{ flex: 1, p: 1.5, display: "grid", gap: 2 }}>

                        <Box sx={{ display: "grid", gridTemplateColumns: "0.5fr 0.5fr", alignItems: "center", gap: 1 }}>
                            <Typography
                                sx={{
                                    fontSize: "13px",
                                    fontWeight: 400,
                                    color: "#1A73E8",
                                    fontFamily: "Outfit, sans-serif",
                                    whiteSpace: "nowrap"
                                }}
                            >
                                Planned Progress
                            </Typography>

                            <Box sx={{ position: "relative", width: "100%" }}>
                                <RoundedLinearProgress
                                    variant="determinate"
                                    value={100}
                                    barcolor="#409CFF"
                                />

                                <Typography
                                    sx={{
                                        position: "absolute",
                                        right: 10,
                                        top: 0,
                                        color: "#fff",
                                        fontWeight: 600,
                                        fontSize: "12px",
                                    }}
                                >
                                    {apiData?.planProgress}%
                                </Typography>
                            </Box>
                        </Box>


                        <Box sx={{ display: "grid", gridTemplateColumns: "0.5fr 0.5fr", alignItems: "center", gap: 1 }}>
                            <Typography
                                sx={{
                                    fontSize: "13px",
                                    fontWeight: 400,
                                    color: "#5F6368",
                                    fontFamily: "Outfit, sans-serif",
                                    whiteSpace: "nowrap"
                                }}
                            >
                                Actual Progress
                            </Typography>

                            <Box sx={{ position: "relative", width: "100%" }}>
                                <RoundedLinearProgress
                                    variant="determinate"
                                    value={
                                        apiData?.planProgress > 0
                                            ? Math.min((apiData?.actualProgress / apiData?.planProgress) * 100, 100)
                                            : 0
                                    }
                                    barcolor="#D66A38"
                                />

                                <Typography
                                    sx={{
                                        position: "absolute",
                                        right: 10,
                                        top: 0,
                                        color: "#fff",
                                        fontWeight: 600,
                                        fontSize: "12px",
                                    }}
                                >
                                    {apiData?.actualProgress}%
                                </Typography>
                            </Box>
                        </Box>

                    </Box>

                    {/* <Box sx={{
                        border: "1px solid #E5E5E5",
                        p: 2,
                        ml: 2,
                        mt: "30px"
                    }}>
                        <Typography sx={{
                            fontFamily: "Outfit, sans-serif",
                            fontWeight: 600,
                            fontSize: '24px',
                            letterSpacing: "0px",
                            color: '#7D7F81'
                        }}> {totalMilestones} <Typography sx={{ fontSize: "14px" }} component={"span"}> Milestones</Typography></Typography>
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography sx={{
                                fontFamily: "Outfit, sans-serif",
                                fontWeight: 600,
                                fontSize: '24px',
                                letterSpacing: "0px",
                                color: '#1A932E'
                            }}>{completedlMilestones}
                                <Typography sx={{ fontSize: "14px" }} component={"span"}> Completed</Typography>
                            </Typography>
                            <Typography sx={{
                                fontFamily: "Outfit, sans-serif",
                                fontWeight: 600,
                                fontSize: '24px',
                                letterSpacing: "0px",
                                color: '#FF383C'
                            }}>{missedMilestones}
                                <Typography sx={{ fontSize: "14px" }} component={"span"}> Missed</Typography>
                            </Typography>
                        </Box>
                    </Box> */}
                </Grid>

                <Grid size={{ xs: 12, md: 7 }}>
                    <HighchartsReact highcharts={Highcharts} options={chartOptions} ref={chartRef} />
                    <Typography sx={{
                        fontFamily: "Outfit, sans-serif",
                        fontWeight: 700,
                        fontSize: '14px',
                        letterSpacing: "0px",
                        color: "#9A9A9A",
                        textAlign: "center"
                    }}>Schedule Performance Index</Typography>
                </Grid>
            </Grid>}
            {isLoading && (
                <Grid item sx={{ flex: "0 0 auto", display: 'flex', alignItems: 'center', px: 3 }}>
                    <Box>Loading Data...</Box>
                </Grid>
            )}
            {error && (
                <Grid item xs={12} sx={{ height: "100%", p: 2 }}>
                    <Box>
                        Error loading data: {error?.status} - {error?.data?.message || "Please try again."}
                    </Box>
                </Grid>
            )}


            {assetError && <AssetError />}

        </Card>
    );
}

export default Schedule
